/**
 * TerminationChecklistList.tsx
 *
 * React-admin resource page that lists all open termination checklist items.
 * Users with the `terminations.manage` permission can confirm individual items
 * directly from this list.
 *
 * Route: /terminations
 */

import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    SelectInput,
    FunctionField,
    usePermissions,
    useRefresh,
    useNotify,
    useRedirect,
    useGetIdentity,
    useListContext,
} from 'react-admin';
import {
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    FormControlLabel,
    Switch,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { confirmTerminationItem, uploadTerminationProof } from '../dataProvider';
import { ImageProofDropzone } from './ImageProofDropzone';

/** The one task type that uses the proof-gated pending -> processing -> done flow. */
export const PROOF_GATED_TASK_TYPE = 'social_insurance_closing';

/**
 * Formats a `task_type` slug (e.g. `social_insurance_closing`) as a
 * human-readable title (e.g. `Social Insurance Closing`).
 */
export const formatTaskType = (taskType: string): string =>
    (taskType ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

/**
 * Renders a coloured status chip for a termination checklist item.
 *
 * @param props.status - `'pending'`, `'processing'`, or `'done'`.
 */
export const StatusChip: React.FC<{ status: string }> = ({ status }) => (
    <Chip
        label={status}
        size="small"
        color={status === 'done' ? 'success' : status === 'processing' ? 'info' : 'warning'}
        data-testid={`status-chip-${status}`}
    />
);

/**
 * Renders a "Confirm" button for pending checklist items. Clicking it opens a
 * confirmation dialog; the actual confirm call only fires from the dialog's
 * own Confirm action.
 *
 * Only visible when the current user holds the `terminations.manage` permission
 * and the item is still `pending`.
 */
export const ConfirmButton: React.FC<{ record: any }> = ({ record }) => {
    const { permissions } = usePermissions<string[]>();
    const refresh = useRefresh();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    if (!Array.isArray(permissions) || !permissions.includes('terminations.manage')) return null;
    if (record?.status !== 'pending') return null;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await confirmTerminationItem(record.id);
            notify('Task confirmed', { type: 'success' });
            setDialogOpen(false);
            refresh();
        } catch (err) {
            notify((err as Error).message ?? 'Confirmation failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => setDialogOpen(true)}
                data-testid={`confirm-btn-${record.id}`}
            >
                Confirm
            </Button>
            <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)}>
                <DialogTitle>Confirm task</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to mark this task as completed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleConfirm}
                        disabled={loading}
                        data-testid={`confirm-dialog-btn-${record.id}`}
                    >
                        {loading ? 'Confirming…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

/**
 * Compact "View" button that navigates to the detail page for a single
 * checklist item (`/terminations/:id/show`), where the full task record and
 * any uploaded proof images can be inspected.
 */
const ViewDetailButton: React.FC<{ record: any }> = ({ record }) => {
    const redirect = useRedirect();

    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => redirect('show', 'terminations', record.id)}
            data-testid={`view-btn-${record.id}`}
        >
            View
        </Button>
    );
};

/**
 * Renders the proof-gated `pending -> processing -> done` control for the
 * `social_insurance_closing` task type, in place of the plain `ConfirmButton`
 * used by the three other task types.
 *
 * - `pending`    — a compact "Mark Processing" button opens a dialog requiring
 *                  a processing-proof image before advancing to `processing`.
 * - `processing` — a compact "Mark Done" button opens a dialog requiring a
 *                  done-proof image before advancing to `done`.
 * - `done`       — nothing to do; renders nothing. Uploaded proof images can
 *                  be reviewed from the item's detail page (`TerminationChecklistShow.tsx`).
 */
export const SocialInsuranceProofControl: React.FC<{ record: any }> = ({ record }) => {
    const { permissions } = usePermissions<string[]>();
    const refresh = useRefresh();
    const notify = useNotify();
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const canManage = Array.isArray(permissions) && permissions.includes('terminations.manage');
    const status = record?.status ?? 'pending';

    const closeDialog = () => {
        if (loading) return;
        setDialogOpen(false);
        setFile(null);
    };

    const handleAdvance = async (targetStatus: 'processing' | 'done') => {
        if (!file) return;
        setLoading(true);
        try {
            await uploadTerminationProof(record.id, targetStatus, file);
            notify(targetStatus === 'processing' ? 'Marked as processing' : 'Task confirmed', { type: 'success' });
            setFile(null);
            setDialogOpen(false);
            refresh();
        } catch (err) {
            notify((err as Error).message ?? 'Upload failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (status === 'done' || !canManage) return null;

    const targetStatus: 'processing' | 'done' = status === 'processing' ? 'done' : 'processing';
    const buttonLabel = targetStatus === 'processing' ? 'Mark Processing' : 'Mark Done';
    const buttonColor = targetStatus === 'processing' ? 'primary' : 'success';

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                color={buttonColor}
                startIcon={<CheckIcon />}
                onClick={() => setDialogOpen(true)}
                data-testid={`${targetStatus === 'processing' ? 'mark-processing' : 'mark-done'}-btn-${record.id}`}
            >
                {buttonLabel}
            </Button>
            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{buttonLabel}</DialogTitle>
                <DialogContent>
                    <ImageProofDropzone
                        label={targetStatus === 'processing' ? 'Processing proof' : 'Done proof'}
                        onFileReady={setFile}
                        disabled={loading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={buttonColor}
                        onClick={() => handleAdvance(targetStatus)}
                        disabled={!file || loading}
                        data-testid={`${targetStatus === 'processing' ? 'mark-processing' : 'mark-done'}-dialog-btn-${record.id}`}
                    >
                        {loading ? 'Uploading…' : buttonLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

/**
 * Status choices for the checklist's status filter.
 *
 * `not_done` is a sentinel value (not a real `status` column value) that the
 * backend translates into `status != 'done'`, letting a user filter out
 * completed items without picking `pending` or `processing` individually.
 */
const statusChoices = [
    { id: 'not_done', name: 'Not Done' },
    { id: 'pending', name: 'Pending' },
    { id: 'processing', name: 'Processing' },
    { id: 'done', name: 'Done' },
];

/**
 * Filter inputs rendered above the termination checklist.
 *
 * - **Employee Name** (`full_name`) — free-text search, matched
 *   accent-insensitively server-side (same `normalizeVietnamese` /
 *   `users.norm_name` approach as `/employees`), so searching "Nguyen"
 *   also matches "Nguyễn".
 * - **Status** (`status`) — select Pending, Processing, Done, or Any
 *   (default; no filter sent).
 */
const terminationFilters = [
    <TextInput key="full_name" source="full_name" label="Employee Name" alwaysOn />,
    <SelectInput key="status" source="status" label="Status" choices={statusChoices} emptyText="Any" alwaysOn />,
];

/**
 * "My tasks only" toggle. Switches the list's `assignee_email` filter
 * between unset (all tasks) and the current logged-in user's email, so a
 * user can quickly narrow the checklist down to just the tasks assigned to
 * them.
 *
 * Reads the current user's email via react-admin's `useGetIdentity` (backed
 * by `GET /whoami`) and toggles the filter via `useListContext`'s
 * `setFilters` — no backend change is needed since `assignee_email` is
 * already an allow-listed, LIKE-matched filter column.
 */
const MyTasksToggle: React.FC = () => {
    const { identity } = useGetIdentity();
    const { filterValues, setFilters, displayedFilters } = useListContext();

    const currentUserEmail = identity?.email;
    const checked = Boolean(currentUserEmail) && filterValues.assignee_email === currentUserEmail;

    const handleChange = (_: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        const { assignee_email: _assigneeEmail, ...rest } = filterValues;
        if (isChecked && currentUserEmail) {
            setFilters({ ...rest, assignee_email: currentUserEmail }, displayedFilters);
        } else {
            setFilters(rest, displayedFilters);
        }
    };

    return (
        <FormControlLabel
            label="My tasks only"
            control={
                <Switch
                    checked={checked}
                    onChange={handleChange}
                    disabled={!currentUserEmail}
                    data-testid="my-tasks-toggle"
                />
            }
        />
    );
};

/**
 * Lists all termination checklist items.
 *
 * Columns displayed:
 * - Employee (full_name / employee_id)
 * - Task Type
 * - Assignee Email
 * - Due Date
 * - Status (chip — `pending` / `processing` / `done`)
 * - Completed By / Completed Date
 * - Action — plain Confirm button for the three legacy task types; for
 *   `social_insurance_closing`, the proof-gated upload control instead
 *   (requires an image for both pending->processing and processing->done)
 * - Details — a "View" button that opens the item's detail page
 *   (see `TerminationChecklistShow.tsx`), where proof images can be inspected.
 *
 * Supports filtering by employee name (accent-insensitive) and status, plus
 * a "My tasks only" toggle that narrows the list to items assigned to the
 * current logged-in user.
 *
 * Requires the `terminations.read` permission (enforced via App.tsx resource
 * gate and the backend).  Bulk-delete is disabled.
 *
 * @returns A react-admin `<List>` showing the termination checklist.
 */
export const TerminationChecklistList: React.FC = () => (
    <List filters={terminationFilters} actions={<MyTasksToggle />}>
        <Datagrid bulkActionButtons={false}>
            <TextField source="full_name" label="Employee" />
            <TextField source="employee_id" label="Employee ID" />
            <FunctionField
                label="Task"
                render={(record: any) => formatTaskType(record?.task_type ?? '')}
            />
            <TextField source="assignee_email" label="Assignee" />
            <TextField source="due_date" label="Due Date" />
            <FunctionField
                label="Status"
                render={(record: any) => <StatusChip status={record?.status ?? 'pending'} />}
            />
            <TextField source="completed_by" label="Completed By" />
            <TextField source="completed_date" label="Completed Date" />
            <FunctionField
                label="Action"
                render={(record: any) =>
                    record?.task_type === PROOF_GATED_TASK_TYPE
                        ? <SocialInsuranceProofControl record={record} />
                        : <ConfirmButton record={record} />
                }
            />
            <FunctionField
                label="Details"
                render={(record: any) => <ViewDetailButton record={record} />}
            />
        </Datagrid>
    </List>
);
